import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Player } from '../types';
import '../styles/components/team-carousel.scss';

// Define Team interface 
interface Team {
  id: string;
  name: string;
  logo: string | null;
  players: Player[];
}

interface TeamCardProps {
  team: Team;
  baseURL: string;
  baseURLnoapi?: string;
}

// TeamCard component embedded within the carousel for better control
const TeamCard: React.FC<TeamCardProps> = ({ team, baseURL, baseURLnoapi = '' }) => {
  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

  // Helper function to get team logo URL
  const getTeamLogoUrl = () => {
    // Handle data URLs (for placeholder teams)
    if (team.logo && team.logo.startsWith('data:')) {
      return team.logo;
    } else if (team.logo && team.logo.startsWith('http')) {
      return team.logo;
    } else if (team.logo) {
      return `${baseURLnoapi || ''}${team.logo}`;
    } else {
      // Get logo from the team logo endpoint
      return `${baseURL}/Team/${team.id}/logo`;
    }
  };

  return (
    <div className="team-card">
      <div className="team-logo">
        <img
          src={getTeamLogoUrl()}
          alt={`${team.name} logo`}
          onError={(e) => {
            // Fallback to placeholder if logo loading fails
            (e.target as HTMLImageElement).src = teamPlaceholderImage;
          }}
        />

        {/* Player roster overlay */}
        <div className="player-roster-overlay">
          <div className="roster-title">Roster</div>
          {team.players && team.players.length > 0 ? (
            <ul className="player-list">
              {team.players.map((player, index) => (
                <li key={player.steamID || index} className="player-item">
                  <Link
                    to={`/profile/${player.steamID}`}
                    className="player-name-link"
                  >
                    {player.playerName}
                  </Link>
                  <span className="player-rating">
                    {player.rating ? player.rating.toFixed(2) : 'N/A'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="no-players">No player data available</div>
          )}
        </div>
      </div>
      <h3>{team.name}</h3>
      {!team.id.startsWith('placeholder-') ? (
        <Link to={`/teams/${team.id}`} className="team-link">
          Team Profile →
        </Link>
      ) : (
        <span className="team-link placeholder-link">
          Coming Soon
        </span>
      )}
    </div>
  );
};

interface TeamCarouselProps {
  teams: Team[];
  isLoading: boolean;
  baseURL: string;
  baseURLnoapi?: string;
  autoPlayInterval?: number; // Time in ms between slides
  teamsPerView?: number; // Number of teams visible at once
}

const TeamCarousel: React.FC<TeamCarouselProps> = ({
  teams,
  isLoading,
  baseURL,
  baseURLnoapi,
  autoPlayInterval = 2000, // Default to 5 seconds
  teamsPerView = 4 // Default to 4 teams per view
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [responsiveTeamsPerView, setResponsiveTeamsPerView] = useState(teamsPerView);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Detect screen size and adjust teamsPerView accordingly
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setResponsiveTeamsPerView(1); // Mobile: 1 team
      } else if (width < 992) {
        setResponsiveTeamsPerView(2); // Tablet: 2 teams
      } else if (width < 1200) {
        setResponsiveTeamsPerView(3); // Small desktop: 3 teams
      } else {
        setResponsiveTeamsPerView(teamsPerView); // Full desktop: use prop
      }
    };

    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [teamsPerView]);
  
  // Create infinite carousel array by duplicating teams if needed
  const getCarouselItems = () => {
    if (teams.length === 0) return [];
    if (teams.length < responsiveTeamsPerView * 2) {
      // If we don't have enough teams to make a good infinite carousel,
      // duplicate them to have at least 2 complete sets
      return [...teams, ...teams, ...teams];
    }
    // Otherwise, add one full loop plus a few extra for smooth infinite scrolling
    return [...teams, ...teams.slice(0, responsiveTeamsPerView)];
  };
  
  const carouselItems = getCarouselItems();
  
  // Function to go to next slide with animation
  const nextSlide = () => {
    if (isAnimating || teams.length === 0) return;
    
    setIsAnimating(true);
    
    // Move to next slide
    setCurrentIndex(prevIndex => {
      const nextIndex = prevIndex + 1;
      
      // If we've reached the end of the original set, jump back to start
      // but we'll do this after animation completes
      if (nextIndex >= teams.length) {
        setTimeout(() => {
          // This timeout should be slightly longer than the animation duration
          // to ensure the jump happens after the animation completes
          setIsAnimating(false);
          setCurrentIndex(0);
        }, 50);
        return nextIndex;
      }
      
      setTimeout(() => setIsAnimating(false), 500);
      return nextIndex;
    });
  };
  
  // Function to go to previous slide with animation
  const prevSlide = () => {
    if (isAnimating || teams.length === 0) return;
    
    setIsAnimating(true);
    
    // Move to previous slide
    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex - 1;
      
      // If we're at the beginning, jump to end
      // but we'll do this after animation completes
      if (newIndex < 0) {
        setTimeout(() => {
          setIsAnimating(false);
          setCurrentIndex(teams.length - 1);
        }, 50);
        return newIndex;
      }
      
      setTimeout(() => setIsAnimating(false), 500);
      return newIndex;
    });
  };
  
  // Handle automatic cycling
  useEffect(() => {
    if (isLoading || isPaused || teams.length <= responsiveTeamsPerView || isAnimating) return;

    const interval = setInterval(() => {
      nextSlide();
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isLoading, isPaused, teams.length, autoPlayInterval, responsiveTeamsPerView, isAnimating]);
  
  // Pause carousel on hover
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);
  
  // Calculate if we should show navigation
  const shouldShowNavigation = teams.length > responsiveTeamsPerView;
  
  if (isLoading) {
    return (
      <div className="team-section-wrapper">
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
        
        <div className="more-teams">
          <Link to="/teams" className="btn btn-outline">View All Teams</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="team-section-wrapper">
      <div 
        className="team-carousel" 
        ref={carouselRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {shouldShowNavigation && (
          <button 
            className="carousel-control carousel-control-prev" 
            onClick={prevSlide}
            aria-label="Previous teams"
            disabled={isAnimating}
          >
            <span>‹</span>
          </button>
        )}
        
        <div className="carousel-track-container">
          <div
            className="carousel-track"
            style={{
              transform: `translateX(-${currentIndex * (100 / responsiveTeamsPerView)}%)`,
              transition: isAnimating ? 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)' : 'none'
            }}
          >
            {carouselItems.map((team, index) => (
              <div className="carousel-item" key={`${team.id}-${index}`}>
                <TeamCard 
                  team={team} 
                  baseURL={baseURL} 
                  baseURLnoapi={baseURLnoapi} 
                />
              </div>
            ))}
          </div>
        </div>
        
        {shouldShowNavigation && (
          <button 
            className="carousel-control carousel-control-next" 
            onClick={nextSlide}
            aria-label="Next teams"
            disabled={isAnimating}
          >
            <span>›</span>
          </button>
        )}
        
        {shouldShowNavigation && teams.length > responsiveTeamsPerView && (
          <div className="carousel-indicators">
            {Array.from({ length: Math.min(teams.length, 8) }).map((_, index) => (
              <button
                key={index}
                className={`carousel-indicator ${currentIndex % teams.length === index ? 'active' : ''}`}
                onClick={() => {
                  if (!isAnimating) {
                    setIsAnimating(true);
                    setCurrentIndex(index);
                    setTimeout(() => setIsAnimating(false), 500);
                  }
                }}
                aria-label={`Go to slide ${index + 1}`}
                disabled={isAnimating}
              />
            ))}
          </div>
        )}
      </div>
      
      <div className="more-teams">
        <Link to="/teams" className="btn btn-outline">View All Teams</Link>
      </div>
    </div>
  );
};

export default TeamCarousel;